import { Injectable } from '@angular/core';
import { AuthentificationService } from '@app/authentification/authentification.service';
import { HttpApiService } from '@app/providers/http-api/http-api.service';
import { forkJoin, Observable, of, map, switchMap, tap } from 'rxjs';
import { Dao } from '../dao';
import { DataLoader } from '../data-loader';
import { DataLoaderAuthentified } from '../data-loader-authentified';

export interface Earning {
  id?: number | null;
  accountId?: number;
  userId: number;
  professionId?: number | null;
  label?: string | null;
  type: string;
  amount: number;
  fraisReel?: number | null;
  frequency: string;
  startDate?: string | null;
  endDate?: string | null;
  aiDateCreated?: string | null;
  monthlyAmount?: number;
  current?: boolean;
}

export interface Profession {
  id?: number | null;
  accountId?: number;
  userId: number;
  type: string;
  label: string;
  companyName?: string;
  situation?: string;
  groupe?: string;
  categorie?: string;
  activitySector?: string;
  estimatedEvolution?: number;
  status?: string;
  earnings?: Earning[];
  moreThan2Years?: boolean;
  workInPublic?: boolean;
  aiDateCreated?: string | null;
  startDate?: string | null;
  current?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class CareerDao extends Dao {
  private references: DataLoader<any> = new DataLoader<any>(() =>
    this.httpApi.get(`/ref/cashflows`),
  );

  private professions: DataLoaderAuthentified<any[]> =
    new DataLoaderAuthentified<any[]>(this.getAuthentified(), user => {
      return this.httpApi
        .post(
          `/accounts/${user.accountId}/professions/search?frequency=MENS`,
          {},
        )
        .pipe(map(res => res['content']));
    });

  private earnings: DataLoaderAuthentified<any[]> = new DataLoaderAuthentified<
    any[]
  >(this.getAuthentified(), user => {
    return this.httpApi
      .post(`/accounts/${user.accountId}/earnings/search?frequency=MENS`, {})
      .pipe(map(res => res['content']));
  });

  constructor(
    httpApi: HttpApiService,
    authentificationService: AuthentificationService,
  ) {
    super(httpApi, authentificationService);
  }

  /* Professions */
  getProfessions(): Observable<any> {
    return this.professions.get();
  }
  saveProfession(
    entity: any,
    reset: boolean = true,
    onPersisted?: (professionId: number) => void,
  ): Observable<any> {
    let dto = { ...entity };
    return this.httpApi
      .post(`/accounts/${entity.accountId}/professions`, dto)
      .pipe(
        switchMap(profession => {
          onPersisted?.(profession.id);

          if (!entity.earnings || entity.earnings.length === 0) {
            return of(profession).pipe(
              tap(() => {
                if (reset) this?.reset();
              }),
            );
          }

          let nested = [];
          for (let earning of entity.earnings) {
            earning.accountId = profession.accountId;
            earning.professionId = profession.id;
            nested.push(this.saveEarning(earning, false));
          }
          return forkJoin(nested).pipe(
            map(() => profession),
            tap(() => {
              if (reset) this?.reset();
            }),
          );
        }),
      );
  }

  deleteProfession(entity: any): Observable<any> {
    return this.httpApi
      .delete(`/accounts/${entity.accountId}/professions/${entity.id}`)
      .pipe(tap(() => this.professions?.reset()));
  }

  /* Earnings */
  getEarnings(): Observable<any> {
    return this.earnings.get();
  }
  getCurrentEarnings(): Observable<any> {
    return this.earnings
      .get()
      .pipe(map(earnings => earnings.filter(e => e.current)));
  }
  saveEarning(entity: any, reset: boolean = true): Observable<any> {
    return this.httpApi
      .post(`/accounts/${entity.accountId}/earnings`, entity)
      .pipe(
        tap(() => {
          if (reset) this.earnings?.reset();
        }),
      );
  }

  deleteEarning(entity: any): Observable<any> {
    if (!entity.id) return of(null);
    return this.httpApi
      .delete(`/accounts/${entity.accountId}/earnings/${entity.id}`)
      .pipe(
        tap(() => {
          this.earnings?.reset();
          this.professions?.reset();
        }),
      );
  }

  /* Reference */
  getReferences(): Observable<any> {
    return this.references.get();
  }

  getEarningTypes(): Observable<any> {
    return this.getReferences().pipe(map(data => data.earningtypes));
  }

  getSsp(): Observable<any> {
    return this.getReferences().pipe(map(data => data.situations));
  }

  getGsp(): Observable<any> {
    return this.getReferences().pipe(map(data => data.groupes));
  }

  getGspBySsp(ssp: string): Observable<any> {
    return this.getReferences().pipe(
      map(data => data.groupes.filter(groupe => groupe.situation == ssp)),
    );
  }

  getCsp(): Observable<any> {
    return this.getReferences().pipe(map(data => data.categories));
  }

  getCspBySsp(ssp: string): Observable<any> {
    return this.getReferences().pipe(
      map(data =>
        data.categories.filter(categorie => categorie.situation == ssp),
      ),
    );
  }

  getCspByGsp(gsp: string): Observable<any> {
    return this.getReferences().pipe(
      map(data => data.categories.filter(categorie => categorie.groupe == gsp)),
    );
  }

  /* state */
  reset() {
    this.professions?.reset();
    this.earnings?.reset();
  }

  onUserChange(event: any) {
    this?.reset();
  }
}
